import { useEffect, useState } from 'react';
import type { PlayerIndex } from '@tpubg/shared';
import { ACTIONS, ACTION_LABELS, DEFAULT_BINDINGS, RESERVED_KEYS, assignKey, clone, keyLabel, keyName, type Action, type Bindings } from '../input/bindings';
import { PLAYER_STYLES } from '../lib/players';

interface Props {
  bindings: Bindings;
  onChange: (b: Bindings) => void;
  /** Per-game names for actions; when given, only these actions are listed. */
  labels?: Partial<Record<Action, string>>;
}

/** Shows both players' keys; click a key to rebind it (Esc cancels). Conflicting keys swap. */
export function ControlsEditor({ bindings, onChange, labels }: Props) {
  const actions = labels ? ACTIONS.filter((a) => labels[a]) : ACTIONS;
  const nameOf = (a: Action) => labels?.[a] ?? ACTION_LABELS[a];
  const [listening, setListening] = useState<{ player: PlayerIndex; action: Action } | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!listening) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'Escape') {
        setListening(null);
        setMessage('Rebinding cancelled.');
        return;
      }
      if (RESERVED_KEYS.has(e.code) || !e.code) {
        setMessage(`${keyLabel(e.code || e.key)} is reserved. Pick another key.`);
        return;
      }
      onChange(assignKey(bindings, listening.player, listening.action, e.code));
      setMessage(`${PLAYER_STYLES[listening.player].label} ${nameOf(listening.action)} set to ${keyName(e.code)}.`);
      setListening(null);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [listening, bindings, onChange]);

  return (
    <div className="controls-editor">
      <div className="controls-editor__grid">
        {([0, 1] as const).map((p) => (
          <div key={p} className={`controls-col controls-col--p${p + 1}`}>
            <h3 className="controls-col__title">
              <span aria-hidden="true">{PLAYER_STYLES[p].symbol}</span> {PLAYER_STYLES[p].label}{' '}
              <span className="controls-col__color">{PLAYER_STYLES[p].name}</span>
            </h3>
            <ul>
              {actions.map((a) => {
                const active = listening?.player === p && listening.action === a;
                return (
                  <li key={a}>
                    <span>{nameOf(a)}</span>
                    <button
                      type="button"
                      className={`keycap${active ? ' is-listening' : ''}`}
                      aria-label={`${PLAYER_STYLES[p].label} ${nameOf(a)}: ${keyName(bindings[p][a])}. Press to change.`}
                      onClick={() => {
                        setListening({ player: p, action: a });
                        setMessage(`Press a new key for ${PLAYER_STYLES[p].label} ${nameOf(a)}. Esc to cancel.`);
                      }}
                    >
                      {active ? 'Press a key…' : keyLabel(bindings[p][a])}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="controls-editor__foot">
        <p className="muted small" role="status" aria-live="polite">
          {message || 'Click a key to remap it. Some keyboards can’t register many keys at once; if a key stops responding mid-game, remap it.'}
        </p>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => {
            onChange(clone(DEFAULT_BINDINGS));
            setMessage('Controls reset to defaults.');
          }}
        >
          Reset defaults
        </button>
      </div>
    </div>
  );
}

export function ControlsSummary({ bindings }: { bindings: Bindings }) {
  return (
    <p className="controls-summary">
      {([0, 1] as const).map((p) => (
        <span key={p} className={`controls-summary__p controls-summary__p--p${p + 1}`}>
          <strong>
            <span aria-hidden="true">{PLAYER_STYLES[p].symbol}</span> {PLAYER_STYLES[p].label}
          </strong>{' '}
          {[bindings[p].forward, bindings[p].left, bindings[p].back, bindings[p].right].map(keyLabel).join(' ')} · fire {keyLabel(bindings[p].fire)}
        </span>
      ))}
      <span>Esc pause</span>
    </p>
  );
}
